/**
 * ResponseDisplay Component
 * 
 * Displays AI-generated responses with markdown formatting support.
 * Shows loading indicators and error messages as needed.
 * 
 * Requirements: 1.4, 1.5, 10.1
 */

import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface ResponseDisplayProps {
  answer: string | null;
  isLoading: boolean;
  error: string | null;
}

export const ResponseDisplay: React.FC<ResponseDisplayProps> = ({ 
  answer, 
  isLoading, 
  error 
}) => {
  // Show loading indicator during API call
  if (isLoading) {
    return (
      <div className="response-display loading" role="status" aria-live="polite">
        <div className="loading-spinner" aria-hidden="true"></div>
        <p>Getting your answer...</p>
      </div>
    );
  }

  // Display error messages when errors occur
  if (error) {
    return (
      <div className="response-display error" role="alert" aria-live="assertive">
        <div className="error-icon" aria-hidden="true">⚠️</div>
        <p className="error-message">{error}</p>
      </div>
    );
  }

  // Display AI-generated answer with markdown formatting
  if (answer) {
    return (
      <div className="response-display answer" role="region" aria-label="Answer">
        <h3>Answer</h3>
        <div className="answer-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{answer}</ReactMarkdown>
        </div>
      </div>
    );
  }

  // Empty state - no response yet
  return (
    <div className="response-display empty">
      <p className="empty-message">Ask a question to get started!</p>
    </div>
  );
};
